import { ApiError } from '@lib/api/api-response';
import { dealQueryKeys } from '@lib/deals/hooks/deal-query-keys';
import { UploadRejectedError } from '@lib/flows/errors/upload-rejected-error';
import { FlowDocumentProcessingError } from '@lib/flows/hooks/use-flow-document-processing';
import { uploadFlowDocument } from '@lib/flows/utils/upload-flow-document';
import { buildDocumentUploadTelemetryContext } from '@lib/observability/document-upload/build-telemetry-context';
import { captureDocumentUploadErrorClient } from '@lib/observability/document-upload/capture-document-upload-error.client';
import { observeDocumentUploadClient } from '@lib/observability/document-upload/observe-document-upload.client';
import { trackDocumentUploadFailed, trackDocumentUploadStarted, trackDocumentUploadSucceeded } from '@lib/observability/document-upload/track-document-upload.client';
import type { DocumentUploadFailurePhase, DocumentUploadSource } from '@lib/observability/document-upload/types';
import { participantKeys } from '@lib/participants/utils/participant-query-keys';
import { scheduleParticipantAutofillRefresh } from '@lib/participants/utils/schedule-participant-autofill-refresh';
// import { useShowPropagationWarning } from '@lib/submissions/hooks/use-show-propagation-warning';
import { useMutation, useQueryClient } from '@lib/query/client';
import type { ProcessingStageId } from '@lib/upload-processing/types/processing-stage-id';
import type { UploadProcessingProgressUpdate } from '@lib/upload-processing/types/upload-processing-progress-update';
import type { Flow } from '../types/flow';
import type { FlowDocumentProcessingRetryTarget } from '../utils/resolve-flow-document-processing-retry-target';
import { resolveFlowDocumentProcessingRetryTarget } from '../utils/resolve-flow-document-processing-retry-target';
import { flowQueryKeys } from './flow-query-keys';
import { readUploadDocparserSettings } from './use-upload-docparser-settings';
import { readUploadParserCacheSettings } from './use-upload-parser-cache-settings';
import { readUploadSkipParseSettings } from './use-upload-skip-parse-settings';

/**
 * Post-accept waiter failures are processing outcomes, not request transport
 * failures. Caller must skip FlowDocumentProcessingError (already recorded
 * server-side) before invoking this.
 */
function resolveClientUploadFailurePhase(error: unknown): DocumentUploadFailurePhase {
  if (error instanceof ApiError && error.status === 408) {
    return 'pdf_processing';
  }
  return 'client_request';
}
export interface UploadFlowDocumentV2Params {
  files: File[];
  dealId: string;
  packageId: string;
  flowId?: string;
  /** Where in the product this upload was initiated (for Sentry / PostHog). */
  uploadSource?: DocumentUploadSource;
  skipParserCacheRead?: boolean;
  skipParserCacheWrite?: boolean;
  docparserEnabled?: boolean;
  docparserVlmHopOverride?: import('@lib/docparser/types/docparser-vlm-hop-id').DocparserVlmHopId;
  /** Skip field detection. Ignores parser cache and docparser toggles. */
  skipParse?: boolean;
  onStageChange?: (stage: ProcessingStageId) => void;
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void;
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void;
  isTemplate?: boolean;
  earlyNavigateEnabled?: boolean;
  onEarlyNavigate?: (href: string) => void;
}
export interface UploadFlowDocumentBatchParams {
  files: File[];
  dealId: string;
  packageId: string;
  flowId?: string;
  uploadSource?: DocumentUploadSource;
  onStageChange?: (stage: ProcessingStageId) => void;
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void;
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void;
  isTemplate?: boolean;
  earlyNavigateEnabled?: boolean;
  onEarlyNavigate?: (href: string) => void;
  /** Ignored; kept for callers that pass legacy combine/split mode. */
  mode?: 'combine' | 'split';
}
export interface UploadFlowDocumentBatchItem {
  file: File;
  status: 'ready' | 'failed';
  flow?: Flow;
  /** Raw failure text, for logs and telemetry. */
  error?: string;
  /** Set only when the rejection carries copy safe to show the user. */
  userMessage?: string;
  /** Post-accept processing failure — retained for retry-processing (not serialized). */
  processingError?: FlowDocumentProcessingError;
  /** Preserved rejection for retry resolution when progress never fired. */
  originalError?: unknown;
  /** Post-accept processing retry target, or null when re-upload is required. */
  processingRetry?: FlowDocumentProcessingRetryTarget | null;
}
export interface UploadFlowDocumentBatchResult {
  items: UploadFlowDocumentBatchItem[];
  summary: {
    total: number;
    ready: number;
    failed: number;
  };
}
function getClientPathname(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname;
}

/**
 * Uploads one PDF to create a document flow (v2). For multiple files, use
 * `uploadBatch` (one HTTP request per file, same as repeated `mutateAsync`).
 */
export function useUploadFlowDocumentV2() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['flows', 'upload-document-v2'],
    // Stale-Clerk-cookie 401s self-recover via the global QueryCache handler;
    // tag so the genuinely-signed-out case doesn't page Sentry.
    meta: {
      suppressSentryStatuses: [401, 403]
    },
    mutationFn: async (params: UploadFlowDocumentV2Params): Promise<Flow> => {
      // One file per request — uploadFlowDocument is single-file, and a
      // multi-element array would otherwise silently drop entries [1..n].
      // Use `uploadBatch` for multiple files (one flow each).
      const [file, ...rest] = params.files;
      if (!file || rest.length > 0) {
        throw new Error('Upload one file at a time; use uploadBatch for more.');
      }
      const uploadSource = params.uploadSource ?? 'unknown';
      const skipParse = params.skipParse ?? readUploadSkipParseSettings().skipParse;
      const sessionCacheSettings = readUploadParserCacheSettings();
      const sessionDocparserSettings = readUploadDocparserSettings();
      const parserCacheSettings = skipParse ? {
        skipParserCacheRead: false,
        skipParserCacheWrite: false
      } : {
        skipParserCacheRead: params.skipParserCacheRead ?? sessionCacheSettings.skipParserCacheRead,
        skipParserCacheWrite: params.skipParserCacheWrite ?? sessionCacheSettings.skipParserCacheWrite
      };
      // Unset defers to the server-side `enable-docparser` default.
      const docparserEnabled = skipParse ? undefined : params.docparserEnabled ?? sessionDocparserSettings.useDocparserDetection;
      const docparserVlmHopOverride = skipParse || docparserEnabled === false ? undefined : params.docparserVlmHopOverride ?? sessionDocparserSettings.docparserVlmHopOverride;
      const telemetry = buildDocumentUploadTelemetryContext({
        source: uploadSource,
        dealId: params.dealId,
        packageId: params.packageId,
        flowId: params.flowId,
        files: params.files,
        pathname: getClientPathname(),
        skipParserCacheRead: parserCacheSettings.skipParserCacheRead,
        skipParserCacheWrite: parserCacheSettings.skipParserCacheWrite
      });
      observeDocumentUploadClient(telemetry);
      trackDocumentUploadStarted(telemetry);
      try {
        const flow = await uploadFlowDocument({
          file,
          dealId: params.dealId,
          packageId: params.packageId,
          flowId: params.flowId,
          uploadSource,
          skipParserCacheRead: parserCacheSettings.skipParserCacheRead ? true : undefined,
          skipParserCacheWrite: parserCacheSettings.skipParserCacheWrite ? true : undefined,
          docparserEnabled,
          ...(docparserVlmHopOverride ? {
            docparserVlmHopOverride
          } : {}),
          ...(skipParse ? {
            skipParse: true
          } : {}),
          onStageChange: params.onStageChange,
          onProcessingProgress: params.onProcessingProgress,
          onFirstWaveReady: params.onFirstWaveReady,
          isTemplate: params.isTemplate,
          earlyNavigateEnabled: params.earlyNavigateEnabled,
          onEarlyNavigate: params.onEarlyNavigate
        });
        trackDocumentUploadSucceeded({
          ...telemetry,
          flowId: flow.id
        });
        return flow;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        // Worker already emitted server failure telemetry for terminal
        // processing failures — don't double-count on the client.
        if (!(error instanceof FlowDocumentProcessingError)) {
          trackDocumentUploadFailed({
            ...telemetry,
            failurePhase: resolveClientUploadFailurePhase(error),
            errorMessage: message,
            ...(error instanceof ApiError ? {
              httpStatus: error.status
            } : {})
          });
        }
        captureDocumentUploadErrorClient(telemetry, error);
        // Preserve the underlying (already user-facing) message rather than
        // prefixing it — callers surface this text directly to users.
        throw error instanceof ApiError ? error : new ApiError(message, 0);
      }
    },
    onSuccess: flow_0 => {
      // showPropagationWarning(flow);
      queryClient.setQueryData(flowQueryKeys.detail(flow_0.id), flow_0);
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: participantKeys.list(flow_0.id)
      });
      if (flow_0.dealId) {
        queryClient.invalidateQueries({
          queryKey: dealQueryKeys.participantsBase(flow_0.dealId)
        });
      }
      scheduleParticipantAutofillRefresh(queryClient, {
        flowId: flow_0.id,
        dealId: flow_0.dealId
      });
    },
    onError: (_error, variables) => {
      // Accept persists a processing/failed collection before the waiter
      // resolves — refresh lists so the docs UI shows the failed slot.
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys.lists()
      });
      if (variables.flowId) {
        queryClient.invalidateQueries({
          queryKey: flowQueryKeys.detail(variables.flowId)
        });
      }
    }
  });
  const uploadBatch = async (params_0: UploadFlowDocumentBatchParams): Promise<UploadFlowDocumentBatchResult> => {
    const items: UploadFlowDocumentBatchItem[] = [];
    let ready = 0;
    let failed = 0;

    // Batch uploads run one flow per file; navigate only to the first collection
    // that becomes viewable, not once per file in the batch.
    let batchNavigatedEarly = false;
    const onEarlyNavigateOnce = params_0.onEarlyNavigate ? (href: string) => {
      if (batchNavigatedEarly) return;
      batchNavigatedEarly = true;
      params_0.onEarlyNavigate?.(href);
    } : undefined;
    for (const file_0 of params_0.files) {
      try {
        const flow_1 = await mutation.mutateAsync({
          files: [file_0],
          dealId: params_0.dealId,
          packageId: params_0.packageId,
          flowId: params_0.flowId,
          uploadSource: params_0.uploadSource,
          onStageChange: params_0.onStageChange,
          onProcessingProgress: params_0.onProcessingProgress,
          onFirstWaveReady: params_0.onFirstWaveReady,
          isTemplate: params_0.isTemplate,
          earlyNavigateEnabled: params_0.earlyNavigateEnabled,
          onEarlyNavigate: onEarlyNavigateOnce
        });
        items.push({
          file: file_0,
          status: 'ready',
          flow: flow_1
        });
        ready += 1;
      } catch (err) {
        items.push({
          file: file_0,
          status: 'failed',
          error: err instanceof Error ? err.message : String(err),
          originalError: err,
          processingRetry: resolveFlowDocumentProcessingRetryTarget({
            error: err
          }),
          ...(err instanceof UploadRejectedError && {
            userMessage: err.message
          }),
          ...(err instanceof FlowDocumentProcessingError && {
            processingError: err
          })
        });
        failed += 1;
      }
    }
    return {
      items,
      summary: {
        total: params_0.files.length,
        ready,
        failed
      }
    };
  };
  return Object.assign(mutation, {
    uploadBatch
  });
}